import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { CheckCircle2, Clock, FileText, AlertTriangle } from 'lucide-react';
import { ICON_PX } from '../../ui';

const COMPLETION_LABEL = {
  checkbox: 'Check off',
  checkbox_note: 'Add a note',
  checkbox_linked: 'Attach evidence',
};

const STATUS_CONFIG = {
  pending: {
    card: 'bg-white border-slate-200 shadow-sm',
    icon: null,
    textColor: 'text-slate-500',
  },
  available: {
    card: 'bg-white border-[var(--aqua)] shadow-md',
    icon: null,
    textColor: 'text-text-dark',
    pulse: true,
  },
  satisfied: {
    card: 'bg-green-bg border-[var(--green)] shadow-sm',
    icon: CheckCircle2,
    iconColor: 'text-green-nn',
    textColor: 'text-green-nn',
  },
  blocked: {
    card: 'bg-slate-50 border-slate-200 opacity-50',
    icon: Clock,
    iconColor: 'text-slate-400',
    textColor: 'text-slate-400',
  },
  skipped: {
    card: 'bg-slate-50 border-slate-200 opacity-35',
    icon: null,
    textColor: 'text-slate-400',
  },
};

/**
 * Friendly task node for practice managers.
 * Shows step number, task name, completion type in plain English, and status.
 */
function TaskNode({ data, selected }) {
  const {
    label, completionType, estimatedMinutes, status = 'pending',
    satisfiedBy, completionSource, alertLevel, stepNumber,
  } = data;

  const isOrphaned = alertLevel === 'critical';
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const StatusIcon = cfg.icon;
  const isSatisfied = status === 'satisfied';
  const isAvailable = status === 'available';

  return (
    <>
      <Handle type="target" position={Position.Top} className="!bg-[var(--slate)] !w-2.5 !h-2.5 !border-2 !border-white !rounded-full" />

      <div
        className={[
          'rounded-xl border-2 px-4 py-3 min-w-[200px] max-w-[260px] transition-all',
          isOrphaned ? 'bg-red-bg border-[var(--red-critical)] shadow-md' : cfg.card,
          selected ? 'ring-2 ring-aqua ring-offset-2' : '',
          isAvailable ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02]' : '',
          cfg.pulse ? 'animate-subtle-pulse' : '',
        ].join(' ')}
      >
        {/* Step number badge */}
        {stepNumber && (
          <div className={[
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold mb-2 uppercase tracking-wide',
            isSatisfied ? 'bg-[var(--green)]/10 text-green-nn' :
            isAvailable ? 'bg-[var(--aqua)]/10 text-[var(--aqua)]' :
            'bg-slate-100 text-slate-400',
          ].join(' ')}>
            Step {stepNumber}
          </div>
        )}

        {/* Task name */}
        <div className="flex items-start gap-2">
          {isOrphaned ? (
            <AlertTriangle size={ICON_PX.md} className="text-[var(--red-critical)] shrink-0 mt-0.5" />
          ) : StatusIcon ? (
            <StatusIcon size={ICON_PX.md} className={`${cfg.iconColor} shrink-0 mt-0.5`} />
          ) : isAvailable ? (
            <div className="w-4 h-4 rounded-full border-[2.5px] border-[var(--aqua)] shrink-0 mt-0.5" />
          ) : (
            <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0 mt-0.5" />
          )}
          <span className={`text-sm font-medium leading-snug ${cfg.textColor}`}>
            {label}
          </span>
        </div>

        {/* Meta row */}
        <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
          {completionType && (
            <span className="flex items-center gap-1">
              {completionType !== 'checkbox' && <FileText size={10} />}
              {COMPLETION_LABEL[completionType] || 'Check off'}
            </span>
          )}
          {estimatedMinutes && <span>~{estimatedMinutes} min</span>}
        </div>

        {/* Satisfied details */}
        {isSatisfied && satisfiedBy && (
          <div className="mt-2 pt-2 border-t border-[var(--green)]/20 text-xs text-green-nn truncate">
            Done by {satisfiedBy}
            {completionSource && <span className="text-slate-400 ml-1">via {completionSource}</span>}
          </div>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} className="!bg-[var(--slate)] !w-2.5 !h-2.5 !border-2 !border-white !rounded-full" />

      {/* Subtle pulse animation for available nodes */}
      <style>{`
        @keyframes subtle-pulse {
          0%, 100% { box-shadow: 0 4px 6px -1px rgba(0,0,0,.1), 0 0 0 0 rgba(0,188,212,0); }
          50% { box-shadow: 0 4px 6px -1px rgba(0,0,0,.1), 0 0 0 4px rgba(0,188,212,.12); }
        }
        .animate-subtle-pulse { animation: subtle-pulse 2.5s ease-in-out infinite; }
      `}</style>
    </>
  );
}

export default memo(TaskNode);
